import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SystemStatusBanner from '../../components/ui/SystemStatusBanner';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import TrackingInputForm from './components/TrackingInputForm';
import DuplicateWarningModal from './components/DuplicateWarningModal';
import RecentEntriesPanel from './components/RecentEntriesPanel';
import ValidationStatsCard from './components/ValidationStatsCard';
import SuccessNotification from './components/SuccessNotification';
import { supabase } from '../../lib/supabase';

const TrackingNumberEntryValidation = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    entry: null,
    newEntry: null
  });

  const [successNotification, setSuccessNotification] = useState({
    isVisible: false,
    entry: null
  });

  const [stats, setStats] = useState({
    todayEntries: 0,
    todayTrend: 0,
    validated: 0,
    validatedPercentage: 0,
    duplicates: 0,
    duplicatesPercentage: 0,
    avgProcessingTime: 0
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      // Fetch tracking entries from Supabase - replace with actual table query
      const { data, error } = await supabase?.from('tracking_entries')?.select('*')?.order('timestamp', { ascending: false })?.limit(20);

      if (error) throw error;

      setEntries(data || []);
      
      // Calculate stats from data
      if (data) {
        const validated = data?.filter(e => e?.status === 'validated')?.length;
        const duplicates = data?.filter(e => e?.status === 'duplicate')?.length;
        const total = data?.length;
        
        setStats({
          todayEntries: total,
          todayTrend: 0,
          validated,
          validatedPercentage: total > 0 ? Math.round((validated / total) * 100) : 0,
          duplicates,
          duplicatesPercentage: total > 0 ? Math.round((duplicates / total) * 100) : 0,
          avgProcessingTime: 0
        });
      }
    } catch (error) {
      console.error('Error fetching entries:', error?.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const generateEntryId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `ENT-2026-${String(timestamp)?.slice(-6)}${String(random)?.padStart(3, '0')}`;
  };

  const checkDuplicate = (trackingNumber) => {
    return entries?.find(entry => entry?.trackingNumber === trackingNumber);
  };

  const handleSubmit = (newEntry) => {
    const duplicate = checkDuplicate(newEntry?.trackingNumber);
    
    if (duplicate) {
      setDuplicateModal({
        isOpen: true,
        entry: duplicate,
        newEntry: {
          ...newEntry,
          entryId: generateEntryId(),
          user: 'Sarah Chen',
          status: 'duplicate'
        }
      });
    } else {
      const entry = {
        ...newEntry,
        entryId: generateEntryId(),
        user: 'Sarah Chen',
        status: 'validated'
      };
      
      setEntries(prev => [entry, ...prev]?.slice(0, 20));
      setSuccessNotification({
        isVisible: true,
        entry
      });
      
      setStats(prev => ({
        ...prev,
        todayEntries: prev?.todayEntries + 1,
        validated: prev?.validated + 1,
        validatedPercentage: Math.round(((prev?.validated + 1) / (prev?.todayEntries + 1)) * 100)
      }));
    }
  };

  const handleBulkSubmit = (trackingNumbers) => {
    const newEntries = [];
    const duplicateCount = { count: 0 };
    
    trackingNumbers?.forEach(trackingNumber => {
      const duplicate = checkDuplicate(trackingNumber);
      
      if (!duplicate) {
        newEntries?.push({
          entryId: generateEntryId(),
          trackingNumber,
          carrier: 'AUTO',
          timestamp: new Date()?.toISOString(),
          user: 'Sarah Chen',
          status: 'validated'
        });
      } else {
        duplicateCount.count++;
      }
    });

    if (newEntries?.length > 0) {
      setEntries(prev => [...newEntries, ...prev]?.slice(0, 20));
      setStats(prev => ({
        ...prev,
        todayEntries: prev?.todayEntries + newEntries?.length,
        validated: prev?.validated + newEntries?.length,
        duplicates: prev?.duplicates + duplicateCount?.count,
        validatedPercentage: Math.round(((prev?.validated + newEntries?.length) / (prev?.todayEntries + newEntries?.length + duplicateCount?.count)) * 100),
        duplicatesPercentage: Math.round(((prev?.duplicates + duplicateCount?.count) / (prev?.todayEntries + newEntries?.length + duplicateCount?.count)) * 100)
      }));
    }
  };

  const handleOverride = () => {
    if (duplicateModal?.newEntry) {
      setEntries(prev => [duplicateModal?.newEntry, ...prev]?.slice(0, 20));
      setStats(prev => ({
        ...prev,
        todayEntries: prev?.todayEntries + 1,
        duplicates: prev?.duplicates + 1,
        duplicatesPercentage: Math.round(((prev?.duplicates + 1) / (prev?.todayEntries + 1)) * 100)
      }));
    }
    setDuplicateModal({ isOpen: false, entry: null, newEntry: null });
  };

  const handleUndo = (entryId) => {
    const entry = entries?.find(e => e?.entryId === entryId);
    if (entry) {
      setEntries(prev => prev?.filter(e => e?.entryId !== entryId));
      setStats(prev => ({
        ...prev,
        todayEntries: prev?.todayEntries - 1,
        validated: entry?.status === 'validated' ? prev?.validated - 1 : prev?.validated,
        duplicates: entry?.status === 'duplicate' ? prev?.duplicates - 1 : prev?.duplicates,
        validatedPercentage: Math.round(((entry?.status === 'validated' ? prev?.validated - 1 : prev?.validated) / (prev?.todayEntries - 1)) * 100),
        duplicatesPercentage: Math.round(((entry?.status === 'duplicate' ? prev?.duplicates - 1 : prev?.duplicates) / (prev?.todayEntries - 1)) * 100)
      }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Tracking Number Entry & Validation | TrackingManager</title>
        <meta name="description" content="Validate and process tracking numbers with real-time duplicate detection and format validation" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <SystemStatusBanner />
        
        <main className="pt-[60px]">
          <div className="max-w-screen-2xl mx-auto px-18 md:px-24 lg:px-32 py-32 md:py-40 lg:py-48">
            <div className="mb-32 md:mb-40">
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-12">
                Tracking Number Entry & Validation
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground max-w-3xl">
                Enter tracking numbers for real-time validation and duplicate detection. System automatically validates format and checks against existing shipments.
              </p>
            </div>

            <ValidationStatsCard stats={stats} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-24 md:gap-32 mt-32 md:mt-40">
              <div className="lg:col-span-2">
                <TrackingInputForm
                  onSubmit={handleSubmit}
                  onBulkSubmit={handleBulkSubmit}
                  recentEntries={entries}
                />
              </div>

              <div className="lg:col-span-1">
                <RecentEntriesPanel
                  entries={entries}
                  onUndo={handleUndo}
                />
              </div>
            </div>
          </div>
        </main>

        <DuplicateWarningModal
          isOpen={duplicateModal?.isOpen}
          duplicateEntry={duplicateModal?.entry}
          onOverride={handleOverride}
          onCancel={() => setDuplicateModal({ isOpen: false, entry: null, newEntry: null })}
        />

        <SuccessNotification
          isVisible={successNotification?.isVisible}
          entry={successNotification?.entry}
          onClose={() => setSuccessNotification({ isVisible: false, entry: null })}
        />

        <QuickActionToolbar />
      </div>
    </>
  );
};

export default TrackingNumberEntryValidation;